import { Checkbox } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { CatalogItemSource } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { fetchOrder } from 'entities/Order';
import { getSelectedOrderId } from 'entities/Order/model/selectors/ordersSelectors';
import { getUserCanEditPropertyCatalog } from 'entities/User/model/selectors/userPermissionsSeletors';
import { AddCatalogItemWithQty } from 'features/AddCatalogItemWithQty';
import {
    addItemsToOrderService,
    buildSingleAddItemData,
} from 'features/AddItemsToOrder';
import { addItemsToOrderActions } from 'features/AddItemsToOrder/model/slices/addItemsToOrderSlice';
import { editOrderItem } from 'features/editItemsInOrder/model/services/editOrderItem/editOrderItem';
import { EditItemsInOrderRequestParams } from 'features/editItemsInOrder/model/types/editItemsInOrderSchema';
import { deleteOrderItem } from 'features/removeItemsInOrder/model/services/deleteOrderItem/deleteOrderItem';
import { getRouteNotFound } from 'shared/const/router';
import { useIsOrdersAddItemsFlow } from 'shared/lib/contexts/CatalogIdentityContext';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useDebouncedCallback } from 'shared/lib/hooks/useDebouncedCallback';

import type { AppDispatch } from 'app/providers/StoreProvider';

interface CatalogItemCheckboxProps {
    isActive: boolean;
    showQtyCheckbox: boolean;
    /** Router state had `selectedOrderId` — gates order API sync + `source` on those requests. */
    quickEdit: boolean;
    isInOrder: boolean;
    qty: number;
    /** Key in `selectedItems` / multiselect map (usually `getItemUid(...)`; see `PropertyCatalogItemDataDefiner`). */
    selectionKey: string;
    /** Actual product uid/uuid */
    itemUid: string;
    /** Property Catalog Id when this row is a Property Catalog item */
    catalogItemId?: string;
    /**
     * Id passed to order APIs: **order line id** when the row is already on the order; otherwise the same
     * stable id as `selectionKey` / `itemUid` (`getItemUid`) so it stays aligned with multiselect keys.
     */
    itemIdToEdit: string;
    isOrderDetails?: boolean;
    disabled?: boolean;
    isAvailable?: boolean;
    source?: CatalogItemSource;
    /** Browse/selection category for `addNewItem` tracking; not the row's property catalog category. */
    categoryId?: string;
    subCategoryId?: string;
    title?: string;
    propertyCategoryId?: string;
}

enum OrderActionType {
    ADD_ITEM = 'add',
    DELETE_ITEM = 'delete',
    EDIT_QTY_ITEM = 'edit',
}

type UpdateOrderForQtyEdit = (
    params: EditItemsInOrderRequestParams,
    action: OrderActionType,
) => void | Promise<void>;

async function syncQuickEditQuantityToOrder(options: {
    dispatch: AppDispatch;
    qty: number;
    id: string;
    itemUid: string;
    selectedOrderId: string | undefined;
    dataToEdit: EditItemsInOrderRequestParams;
    updateOrder: UpdateOrderForQtyEdit;
}): Promise<void> {
    const {
        dispatch,
        qty,
        id,
        itemUid,
        selectedOrderId,
        dataToEdit,
        updateOrder,
    } = options;

    dispatch(addItemsToOrderActions.setIsLoadingAddItemsToOrder(true));
    if (qty >= 1 && qty) {
        if (selectedOrderId) {
            await updateOrder(dataToEdit, OrderActionType.EDIT_QTY_ITEM);
        }
    } else if (qty <= 0) {
        dispatch(catalogItemActions.removeMultiselectItem(id));
        dispatch(catalogItemActions.removeNewItem(itemUid));
        if (selectedOrderId) {
            await updateOrder(dataToEdit, OrderActionType.DELETE_ITEM);
        }
    }
    dispatch(addItemsToOrderActions.setIsLoadingAddItemsToOrder(false));
}

async function toggleSelectAddToMultiselect(options: {
    dispatch: AppDispatch;
    id: string;
    itemUid: string;
    catalogItemId?: string;
    title?: string;
    propertyCategoryId?: string;
    trackNewAddedItems: boolean;
    source?: CatalogItemSource;
    categoryId?: string;
    subCategoryId?: string;
    quickEdit: boolean;
    selectedOrderId: string | undefined;
    dataToEdit: EditItemsInOrderRequestParams;
    updateOrder: UpdateOrderForQtyEdit;
}): Promise<void> {
    const {
        dispatch,
        id,
        itemUid,
        catalogItemId,
        title,
        propertyCategoryId,
        trackNewAddedItems,
        source,
        categoryId,
        subCategoryId,
        quickEdit,
        selectedOrderId,
        dataToEdit,
        updateOrder,
    } = options;

    dispatch(
        catalogItemActions.addMultiselectItem({
            id,
            uid: itemUid,
            catalogItemId,
            ...(title !== undefined && { title }),
            ...(propertyCategoryId !== undefined && {
                propertyCategoryId,
            }),
        }),
    );

    if (trackNewAddedItems) {
        dispatch(
            catalogItemActions.addNewItem({
                itemId: id,
                source,
                categoryId,
                subCategoryId,
            }),
        );
    }

    if (quickEdit && selectedOrderId) {
        await updateOrder({ ...dataToEdit, qty: 1 }, OrderActionType.ADD_ITEM);
    }
}

async function toggleSelectRemoveFromMultiselect(options: {
    dispatch: AppDispatch;
    id: string;
    itemIdToEdit: string;
    trackNewAddedItems: boolean;
    quickEdit: boolean;
    selectedOrderId: string | undefined;
    dataToEdit: EditItemsInOrderRequestParams;
    updateOrder: UpdateOrderForQtyEdit;
}): Promise<void> {
    const {
        dispatch,
        id,
        itemIdToEdit,
        trackNewAddedItems,
        quickEdit,
        selectedOrderId,
        dataToEdit,
        updateOrder,
    } = options;

    dispatch(catalogItemActions.removeMultiselectItem(id));

    if (quickEdit && selectedOrderId) {
        await updateOrder(
            { ...dataToEdit, qty: 0 },
            OrderActionType.DELETE_ITEM,
        );
    }

    if (trackNewAddedItems && id === itemIdToEdit) {
        dispatch(catalogItemActions.removeItem(id));
    }
}

export const CatalogItemCheckbox = ({
    isActive,
    showQtyCheckbox,
    selectionKey,
    itemUid,
    catalogItemId,
    itemIdToEdit,
    quickEdit,
    isInOrder,
    qty,
    isOrderDetails,
    disabled = false,
    isAvailable = true,
    source,
    categoryId,
    subCategoryId,
    title,
    propertyCategoryId,
}: CatalogItemCheckboxProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const canEditCatalog = useSelector(getUserCanEditPropertyCatalog);
    const selectedOrderId = useSelector(getSelectedOrderId);
    const trackNewAddedItems = useIsOrdersAddItemsFlow();

    // Always use itemIdToEdit, unless we are in order edit mode (quickEdit) and it's a property catalog item
    const apiEditId = useMemo(
        () =>
            source === 'marketplace' || !quickEdit || isInOrder
                ? itemIdToEdit
                : catalogItemId ?? itemIdToEdit,
        [source, quickEdit, isInOrder, catalogItemId, itemIdToEdit],
    );

    const [loading, setLoading] = useState(false);

    const redirectOnNotFound = useCallback(
        () => navigate(getRouteNotFound()),
        [navigate],
    );

    const updateOrder = useDebouncedCallback(
        async (
            params: EditItemsInOrderRequestParams,
            action: OrderActionType,
        ) => {
            dispatch(addItemsToOrderActions.setIsLoadingAddItemsToOrder(true));
            setLoading(true);
            switch (action) {
                case OrderActionType.ADD_ITEM:
                    await dispatch(
                        addItemsToOrderService({
                            data: buildSingleAddItemData(source, {
                                itemId: params.itemId,
                                qty: params.qty,
                            }),
                            orderId: params.orderId,
                        }),
                    );
                    break;

                case OrderActionType.DELETE_ITEM:
                    await dispatch(deleteOrderItem(params));
                    break;

                case OrderActionType.EDIT_QTY_ITEM:
                    await dispatch(editOrderItem(params));
                    break;
                default:
                    break;
            }
            isOrderDetails &&
                (await dispatch(
                    fetchOrder({
                        id: selectedOrderId,
                        onNotFound: redirectOnNotFound,
                    }),
                ));
            dispatch(addItemsToOrderActions.setIsLoadingAddItemsToOrder(false));
            setLoading(false);
        },
    );

    const onItemToggleSelect = useCallback(
        (id: string) => async () => {
            const dataToEdit = {
                orderId: selectedOrderId,
                itemId: apiEditId,
                qty,
                ...(quickEdit && { source }),
            };

            const isSelecting = (!qty || qty === 0) && !isInOrder;

            if (isSelecting) {
                await toggleSelectAddToMultiselect({
                    dispatch,
                    id,
                    itemUid,
                    catalogItemId,
                    title,
                    propertyCategoryId,
                    trackNewAddedItems,
                    source,
                    categoryId,
                    subCategoryId,
                    quickEdit,
                    selectedOrderId,
                    dataToEdit,
                    updateOrder,
                });
            } else {
                await toggleSelectRemoveFromMultiselect({
                    dispatch,
                    id,
                    itemIdToEdit,
                    trackNewAddedItems,
                    quickEdit,
                    selectedOrderId,
                    dataToEdit,
                    updateOrder,
                });
            }
        },
        [
            selectedOrderId,
            apiEditId,
            itemIdToEdit,
            qty,
            isInOrder,
            dispatch,
            itemUid,
            catalogItemId,
            source,
            categoryId,
            subCategoryId,
            quickEdit,
            trackNewAddedItems,
            updateOrder,
            title,
            propertyCategoryId,
        ],
    );

    const onSetItemValue = useCallback(
        (id: string) => async (qty: number) => {
            const dataToEdit = {
                orderId: selectedOrderId,
                itemId: apiEditId,
                qty,
                ...(quickEdit && { source }),
            };

            if (qty < 1) {
                if (quickEdit && id === itemIdToEdit) {
                    dispatch(catalogItemActions.removeItem(id));
                } else {
                    dispatch(catalogItemActions.toggleMultiselectItem(id));
                }
            } else {
                dispatch(
                    catalogItemActions.setItemQuantity({ id, value: qty }),
                );
            }

            if (quickEdit) {
                await syncQuickEditQuantityToOrder({
                    dispatch,
                    qty,
                    id,
                    itemUid,
                    selectedOrderId,
                    dataToEdit,
                    updateOrder,
                });
            } else if (trackNewAddedItems && qty <= 0) {
                dispatch(catalogItemActions.removeMultiselectItem(id));
                dispatch(catalogItemActions.removeNewItem(itemUid));
            }
        },
        [
            selectedOrderId,
            apiEditId,
            itemIdToEdit,
            quickEdit,
            trackNewAddedItems,
            source,
            dispatch,
            updateOrder,
            itemUid,
        ],
    );

    return !showQtyCheckbox ? (
        <Checkbox
            disableRipple
            disabled={disabled}
            sx={{
                display: canEditCatalog ? 'inline-flex' : 'none',
                paddingTop: '0px',
                '& >.MuiSvgIcon-root': {
                    width: '28px',
                    height: '28px',
                },
            }}
            checked={isActive}
            onClick={disabled ? undefined : onItemToggleSelect(itemIdToEdit)}
        />
    ) : (
        <AddCatalogItemWithQty
            value={qty}
            active={isActive}
            onItemToggleSelect={onItemToggleSelect(itemUid)}
            onSetValue={onSetItemValue(selectionKey)}
            loading={loading}
            disabledAddButton={!isAvailable}
        />
    );
};
